import React, { useEffect, useState, useRef } from 'react';
import { Monitor, Users, BookOpen, Trophy, ArrowRight, Sparkles } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const EventimeExpo = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const features = [
    {
      icon: Monitor,
      title: t('expo.features.showcase'),
      description: t('expo.features.showcaseDesc')
    },
    {
      icon: Users,
      title: t('expo.features.networking'),
      description: t('expo.features.networkingDesc')
    },
    {
      icon: BookOpen,
      title: t('expo.features.workshops'),
      description: t('expo.features.workshopsDesc')
    },
    {
      icon: Trophy,
      title: t('expo.features.competitions'),
      description: t('expo.features.competitionsDesc')
    }
  ];

  return (
    <section id="expo" className="section section-alt" ref={sectionRef}>
      <div className="container">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-giant)' }}>
          <div 
            className={`animated ${isVisible ? 'fadeIn' : ''}`}
            style={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--spacing-small)',
              marginBottom: 'var(--spacing-medium)'
            }}
          >
            <Sparkles size={32} style={{ color: 'var(--brand-primary)' }} className="floating" />
            <h2 className="heading-1">
              {t('expo.title')}
            </h2>
            <Sparkles size={32} style={{ color: 'var(--brand-primary)' }} className="floating" />
          </div>
          
          <p 
            className={`body-large animated ${isVisible ? 'fadeIn' : ''}`}
            style={{ 
              maxWidth: '600px', 
              margin: '0 auto var(--spacing-medium) auto',
              transitionDelay: '0.2s',
              fontStyle: 'italic',
              fontSize: '1.2rem',
              color: 'var(--brand-dark)',
              fontWeight: '500'
            }}
          >
            {t('expo.subtitle')}
          </p>

          <p 
            className={`body-medium animated ${isVisible ? 'fadeIn' : ''}`}
            style={{ 
              maxWidth: '800px', 
              margin: '0 auto',
              transitionDelay: '0.3s'
            }}
          >
            {t('expo.description')}
          </p>
        </div>

        {/* Features Grid */}
        <div 
          className="network-grid"
          style={{ 
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 'var(--spacing-large)',
            padding: '0',
            marginBottom: 'var(--spacing-giant)'
          }}
        >
          {features.map((feature, index) => {
            const IconComponent = feature.icon;
            
            return (
              <div 
                key={index}
                className={`network-card hover-lift animated ${isVisible ? 'fadeIn' : ''}`}
                style={{ 
                  transitionDelay: `${0.4 + index * 0.1}s`,
                  textAlign: 'center'
                }}
              >
                {/* Icon */}
                <div style={{
                  background: 'linear-gradient(135deg, var(--brand-primary) 0%, #a8ff00 100%)',
                  borderRadius: '20px',
                  padding: 'var(--spacing-medium)',
                  marginBottom: 'var(--spacing-medium)',
                  color: 'var(--brand-dark)',
                  boxShadow: '0 4px 16px rgba(211, 255, 98, 0.3)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <IconComponent size={32} className="icon-enhanced" />
                </div>

                {/* Content */}
                <h3 className="network-card-title" style={{ marginBottom: 'var(--spacing-medium)' }}>
                  {feature.title}
                </h3>
                
                <p className="network-card-content">
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* CTA Section */}
        <div 
          className={`animated ${isVisible ? 'fadeIn' : ''}`}
          style={{
            textAlign: 'center',
            transitionDelay: '0.8s'
          }}
        >
          <div style={{
            background: 'linear-gradient(135deg, var(--brand-primary) 0%, rgba(211, 255, 98, 0.8) 100%)',
            borderRadius: '32px',
            padding: 'var(--spacing-xl)',
            maxWidth: '800px',
            margin: '0 auto',
            border: `3px solid var(--brand-dark)`,
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Decorative elements */}
            <div style={{
              position: 'absolute',
              top: '-10px',
              right: '-10px',
              width: '60px',
              height: '60px',
              background: 'var(--brand-dark)',
              borderRadius: '50%',
              opacity: '0.1'
            }} />
            <div style={{
              position: 'absolute',
              bottom: '-20px',
              left: '-20px',
              width: '80px',
              height: '80px',
              background: 'var(--brand-dark)',
              borderRadius: '50%',
              opacity: '0.05'
            }} />
            
            <h3 
              className="heading-2" 
              style={{ 
                marginBottom: 'var(--spacing-medium)',
                color: 'var(--brand-dark)',
                position: 'relative',
                zIndex: 2
              }}
            >
              {t('expo.readyToJoin')}
            </h3>
            
            <p 
              className="body-large" 
              style={{ 
                marginBottom: 'var(--spacing-large)',
                color: 'var(--brand-dark)',
                opacity: '0.8',
                position: 'relative',
                zIndex: 2
              }}
            >
              {t('expo.joinDescription')}
            </p>
            
            <button 
              className="btn-primary hover-lift"
              onClick={() => {
                const whatsappMessage = t('whatsapp.exhibitorInquiry');
                const encodedMessage = encodeURIComponent(whatsappMessage);
                const whatsappURL = `https://wa.me/+6285179649711?text=${encodedMessage}`;
                window.open(whatsappURL, '_blank');
              }}
              style={{ 
                background: 'var(--brand-dark)',
                fontSize: '1.1rem',
                position: 'relative',
                zIndex: 2
              }}
            >
              <Trophy size={20} className="icon-enhanced" />
              <span>{t('expo.cta')}</span>
              <ArrowRight size={20} className="icon-enhanced" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EventimeExpo;